import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/pages/Login";
import Seo from "@/components/Seo";

export default function Signup() {
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created. Welcome to MeasureTheRoof!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 grid place-items-center px-5 py-12">
      <Seo title="Sign up — MeasureTheRoof" description="Create a free MeasureTheRoof account and order aerial roof measurement reports. Pay per report, no contract." path="/signup" />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="card-mtr">
          <h1 className="font-body font-extrabold text-navy text-[24px] mb-1">Create your account</h1>
          <p className="text-mortar text-[14px] mb-6">Start measuring roofs in minutes. No contract.</p>

          <GoogleButton label="Sign up with Google" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gravel flex-1" /><span className="text-[12px] text-mortar uppercase tracking-wider">or</span><div className="h-px bg-gravel flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="name" className="label-mtr">Full name</label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="name" required value={form.name} onChange={set("name")} className="input-mtr pl-11" placeholder="Jordan Smith" data-testid="signup-name" />
              </div>
            </div>
            <div>
              <label htmlFor="company" className="label-mtr">Company <span className="text-mortar normal-case">(optional)</span></label>
              <div className="relative">
                <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="company" value={form.company_name} onChange={set("company_name")} className="input-mtr pl-11" placeholder="Smith Roofing LLC" data-testid="signup-company" />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="label-mtr">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="email" type="email" required value={form.email} onChange={set("email")} className="input-mtr pl-11" placeholder="you@company.com" data-testid="signup-email" />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="label-mtr">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="password" type="password" required value={form.password} onChange={set("password")} className="input-mtr pl-11" placeholder="At least 8 characters" data-testid="signup-password" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="signup-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Create account"}
            </button>
          </form>
          <p className="text-[12px] text-mortar mt-4 leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="text-orange font-semibold">Terms</Link> and{" "}
            <Link to="/privacy" className="text-orange font-semibold">Privacy Policy</Link>.
          </p>
        </div>
        <p className="text-center text-[14px] text-mortar mt-5">
          Already have an account?{" "}
          <Link to="/login" className="text-orange font-bold" data-testid="go-login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
